import type { DemoStatusResponse } from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lms/ui/table';
import { useToast } from '@lms/ui/use-toast';
import { RefreshCw, SquareArrowOutUpRightIcon, Trash2 } from 'lucide-react';
import { useState, useTransition } from 'react';
import { Link } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import { DemoConfirmDialog } from '@/features/demo/confirm-dialog';
import { disableDemoAction, refreshDemoAction } from '@/features/demo/demo-api';

export function DemoOverview({
  status,
  onChanged,
}: {
  status: DemoStatusResponse;
  onChanged: () => void;
}) {
  const t = useTranslations('demo');
  const { toast } = useToast();
  const [pending, startTransition] = useTransition();
  const [confirmingId, setConfirmingId] = useState<number | null>(null);

  // The Next server actions revalidated the page; here a successful action
  // revalidates the shared demo status via SWR `mutate` (onChanged).
  const onRefreshAll = () =>
    startTransition(async () => {
      const r = await refreshDemoAction();
      if (r.ok) {
        toast({ title: t('refreshed_title') });
        onChanged();
      } else {
        toast({
          title: t('error'),
          description: r.error,
          variant: 'destructive',
        });
      }
    });

  const onDisable = (locationId: number) =>
    startTransition(async () => {
      const r = await disableDemoAction(locationId);
      if (r.ok) {
        toast({ title: t('disabled_title') });
        onChanged();
      } else {
        toast({
          title: t('error'),
          description: r.error,
          variant: 'destructive',
        });
      }
    });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted-foreground text-sm">
          {status.autoRefreshEnabled
            ? t('auto_refresh_on', { days: status.retentionDays })
            : t('auto_refresh_off')}
        </p>
        <Button
          variant="outline"
          onClick={onRefreshAll}
          disabled={pending || status.locations.length === 0}
        >
          <RefreshCw className="h-4 w-4" />
          {t('refresh_all')}
        </Button>
      </div>

      {status.locations.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('overview_empty')}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('location')}</TableHead>
              <TableHead className="text-right">{t('students')}</TableHead>
              <TableHead className="text-right">{t('sessions')}</TableHead>
              <TableHead className="text-right">{t('groups')}</TableHead>
              <TableHead className="w-5" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {status.locations.map((l) => (
              <TableRow key={l.id}>
                <TableCell>
                  <Link
                    to={`/${l.slug}/admin/settings`}
                    className="hover:underline"
                  >
                    {l.name}
                  </Link>
                  <Link
                    to={`/${l.slug}/admin`}
                    target="_blank"
                    className="text-muted-foreground ml-2 inline-block"
                    title={l.slug}
                  >
                    <SquareArrowOutUpRightIcon className="inline-block h-3 w-3" />
                  </Link>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {l.students.toLocaleString()}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {l.sessions.toLocaleString()}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {l.groups.toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setConfirmingId(l.id)}
                    disabled={pending}
                    title={t('disable')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <p className="text-muted-foreground text-xs">{t('overview_hint')}</p>

      <DemoConfirmDialog
        open={confirmingId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmingId(null);
          }
        }}
        title={t('confirm_disable_title')}
        description={t('confirm_disable_description')}
        confirmLabel={t('disable')}
        pending={pending}
        onConfirm={() => {
          const id = confirmingId;
          setConfirmingId(null);
          if (id !== null) {
            onDisable(id);
          }
        }}
      />
    </div>
  );
}
